import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  standardTransition,
} from '../styles';
import { Container, Link, Svg } from './ui';
import Logo from './Logo';
import linkedinIcon from '../images/linkedin-icon.inline.svg';
import twitterIcon from '../images/twitter-icon.inline.svg';
import youtubeIcon from '../images/youtube-icon.inline.svg';

const StyledFooter = styled.footer`
  color: ${standardColours.white};
  background-color: ${brandColours.secondary};
`;

const StyledTop = styled.div`
  display: grid;
  row-gap: 20px;
  padding-top: 60px;

  ${minBreakpointQuery.tsmall`
    column-gap: 30px;
  `}

  ${minBreakpointQuery.small`
    grid-template-columns: repeat(2, 1fr);
    padding-top: 90px;
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${minBreakpointQuery.medium`
    padding-top: 120px;
    column-gap: 40px;
  `}

  ${minBreakpointQuery.large`
    padding-top: 150px;
  `}
`;

const StyledLinksWrapper = styled.nav`
  ${minBreakpointQuery.smedium`
    &:nth-child(-n+2){
      grid-area: 1/auto/3/auto;
    }

    &:nth-child(5){
      grid-area: 1/4;
    }

  `}
`;

const StyledHeading = styled.h3`
  font-weight: ${fontWeights.semibold};
  ${fontSize(18)};
  margin-bottom: 20px;
`;

const StyledLinkItems = styled.ul`
  list-style-type: none;
`;

const StyledLinkItem = styled.li`
  &:not(:last-child) {
    margin-bottom: 15px;
  }
`;

const StyledLink = styled(Link)`
  &:hover {
    text-decoration: underline;
  }
`;

const StyledBadges = styled.div`
  display: flex;
  gap: 24px;

  ${maxBreakpointQuery.small`
    justify-content: center;
  `}

  ${minBreakpointQuery.smedium`
    padding-top: 10px;
    grid-area: 2/4;
  `}
`;

const StyledBadge = styled.img`
  max-width: 108px;
  max-height: 114px;
`;

const StyledMiddle = styled.div`
  display: flex;
  align-items: center;
  margin-top: 60px;
  padding-bottom: 35px;
  border-bottom: solid 1px ${brandColours.senary};

  ${maxBreakpointQuery.smedium`
    flex-direction: column;
    gap: 60px;
  `}

  ${minBreakpointQuery.small`
    margin-top: 90px;
    padding-bottom: 40px;
  `}

  ${minBreakpointQuery.smedium`
    justify-content: space-between;
  `}

  ${minBreakpointQuery.medium`
    margin-top: 120px;
    padding-bottom: 45px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 150px;
  `}

  ${minBreakpointQuery.xlarge`
    padding-bottom: 50px;
  `}
`;

const StyledCertificationLogo = styled.img`
  width: 200px;

  ${minBreakpointQuery.large`
    width: 220px;
  `}
`;

const StyledIconLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;

  ${minBreakpointQuery.small`
    gap: 50px;
  `}

  ${minBreakpointQuery.large`
    gap: 75px;
  `}
`;

const StyledIconLink = styled.a`
  display: block;
  height: 30px;
  width: 30px;

  ${minBreakpointQuery.small`
    height: 35px;
    width: 35px;
  `}

  ${minBreakpointQuery.large`
    height: 40px;
    width: 40px;
  `}
`;

const StyledIcon = styled(Svg)`
  height: 100%;
  width: 100%;
  fill: ${standardColours.white};
`;

const StyledLogo = styled(Logo)`
  filter: brightness(0) invert(1);
  width: 200px;

  ${minBreakpointQuery.large`
    width: 220px;
  `}
`;

const StyledBottom = styled.div`
  padding: 35px 0;
  display: flex;
  ${fontSize(14)};
  color: ${brandColours.senary};

  ${maxBreakpointQuery.small`
    flex-direction: column;
    align-items: center;
  `}

  ${minBreakpointQuery.small`
    padding: 40px 0;
    justify-content: space-between;
    gap: 40px;
  `}

  ${minBreakpointQuery.medium`
    padding: 45px 0;
  `}

  ${minBreakpointQuery.xlarge`
    padding: 50px 0;
  `}
`;

const StyledTextWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 20px;
  column-gap: 30px;
  text-align: center;

  ${maxBreakpointQuery.small`
    justify-content: center;
    margin-bottom: 20px;
  `}

  ${minBreakpointQuery.small`
    column-gap: 35px; 
  `}

  ${minBreakpointQuery.large`
    column-gap: 40px;
  `}
`;

const StyledText = styled.p``;

const StyledPolicyLinkItems = styled.ul`
  list-style-type: none;
  display: flex;
  row-gap: 20px;
  column-gap: 30px;

  ${minBreakpointQuery.small`
    column-gap: 35px; 
  `}

  ${minBreakpointQuery.large`
    column-gap: 40px;
  `}
`;

const StyledPolicyLink = styled(Link)`
  text-decoration: underline;
  transition: ${standardTransition('color')};

  &:hover {
    color: ${standardColours.white};
  }
`;

const Footer = () => {
  const {
    datoCmsFooter: { linkLists, partnerBadges, certificationLogo, policyLinks },
    datoCmsContactInformation: { linkedinLink, twitterLink, youtubeLink },
  } = useStaticQuery(graphql`
    query FooterQuery {
      datoCmsFooter {
        linkLists {
          heading
          links {
            text
            pageUrl {
              ...LinkFragment
            }
          }
        }
        partnerBadges {
          url
          alt
        }
        certificationLogo {
          url
          alt
        }
        policyLinks {
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
      datoCmsContactInformation {
        linkedinLink {
          url
        }
        twitterLink {
          url
        }
        youtubeLink {
          url
        }
      }
    }
  `);

  return (
    <StyledFooter>
      <Container>
        <StyledTop>
          {linkLists.map(({ heading, links }, id) => (
            <StyledLinksWrapper key={id}>
              <StyledHeading>{heading}</StyledHeading>
              <StyledLinkItems>
                {links.map(({ text, pageUrl }, id) => (
                  <StyledLinkItem key={id}>
                    <StyledLink to={pageUrl}>{text}</StyledLink>
                  </StyledLinkItem>
                ))}
              </StyledLinkItems>
            </StyledLinksWrapper>
          ))}
          {partnerBadges.length > 0 && (
            <StyledBadges>
              {partnerBadges.map(({ url, alt }, id) => (
                <StyledBadge key={id} src={url} alt={alt} loading="lazy" />
              ))}
            </StyledBadges>
          )}
        </StyledTop>
        <StyledMiddle>
          <StyledCertificationLogo
            src={certificationLogo.url}
            alt={certificationLogo.alt}
            loading="lazy"
          />
          <StyledIconLinks>
            <StyledIconLink
              href={linkedinLink.url}
              target="_blank"
              rel="noreferrer"
              aria-label="Linkedin Link"
            >
              <StyledIcon image={linkedinIcon} />
            </StyledIconLink>
            <StyledIconLink
              href={twitterLink.url}
              target="_blank"
              rel="noreferrer"
              aria-label="Twitter Link"
            >
              <StyledIcon image={twitterIcon} />
            </StyledIconLink>
            <StyledIconLink
              href={youtubeLink.url}
              target="_blank"
              rel="noreferrer"
              aria-label="Youtube Link"
            >
              <StyledIcon image={youtubeIcon} />
            </StyledIconLink>
          </StyledIconLinks>
          <StyledLogo />
        </StyledMiddle>
        <StyledBottom>
          <StyledTextWrapper>
            <StyledText>
              © MarCloud Limited {new Date().getFullYear()}. All Rights
              Reserved.
            </StyledText>
            <StyledText>MarCloud ® is a registered trade mark.</StyledText>
          </StyledTextWrapper>
          <StyledPolicyLinkItems>
            {policyLinks.map(({ text, pageUrl }, id) => (
              <StyledLinkItem key={id}>
                <StyledPolicyLink to={pageUrl}>{text}</StyledPolicyLink>
              </StyledLinkItem>
            ))}
          </StyledPolicyLinkItems>
        </StyledBottom>
      </Container>
    </StyledFooter>
  );
};

export default Footer;
